import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import { useEffect, useState } from "react";
import { APIProposal, emptyProposal, getProposal } from "../api/proposals";
import {
  APIAddress,
  emptyAddress,
  getBusinessAddress,
  getBusinessLogo,
} from "../api/settings";
import { formatMoney } from "../utils";

const styles = StyleSheet.create({
  page: {
    flexDirection: "column",
    fontSize: 12,
  },
  split: {
    flexDirection: "row",
  },

  addressBox: {
    margin: 10,
    padding: 10,
    width: "50%",
  },

  section: {
    margin: 10,
    padding: 10,
  },

  terms: {
    marginHorizontal: 10,
    padding: 10,
  },

  proposalLine: {
    flexDirection: "row",
  },

  proposalLineInfo: {
    width: "100%",
    border: 0.5,
    padding: 5,
  },

  proposalLineProduct: {},

  proposalLineNotes: {},

  proposalSummary: {
    paddingVertical: 10,
  },

  proposalSummaryLine: {
    flexDirection: "row",
    justifyContent: "flex-end",
  },

  proposalSummaryDesc: {
    textAlign: "right",
  },

  proposalSummaryAmount: {
    width: 100,
    textAlign: "right",
  },

  proposalLineQty: {
    width: 50,
    border: 0.5,
    padding: 5,
  },

  proposalLinePrice: {
    width: 125,
    border: 0.5,
    padding: 5,
  },
  proposalLineTotal: {
    width: 150,
    border: 0.5,
    padding: 5,
  },

  signatureLine: {
    width: "100%",
    borderBottom: 1,
  },

  signatureImg: {
    height: 30,
    width: 150,
  },
});

export default function ProposalPDF({ invoiceId }: { invoiceId: string }) {
  const [proposal, setProposal] = useState<APIProposal>(emptyProposal());
  const [companyDetails, setCompanyDetails] = useState<APIAddress>(
    emptyAddress()
  );
  const [logo, setLogo] = useState("");

  useEffect(() => {
    getProposal(invoiceId || undefined).then((v) => setProposal(v));
    getBusinessAddress().then((v) => setCompanyDetails(v));
    getBusinessLogo().then((v) => {
      setLogo(v);
    });
  }, [invoiceId]);

  return (
    <Document>
      <Page size="LETTER" style={styles.page}>
        <View style={styles.split}>
          <View style={styles.addressBox}>
            <Image
              src={logo}
              style={{ width: "100%", maxHeight: 100, objectFit: "contain" }}
            />
          </View>
          <View style={styles.addressBox}>
            {companyDetails.name && <Text>{companyDetails.name}</Text>}
            <Text>{companyDetails.addressLine1}</Text>
            <Text>{companyDetails.addressLine2}</Text>
            <Text>
              {companyDetails.city}, {companyDetails.state} {companyDetails.zip}
            </Text>
            {companyDetails.phone && <Text>{companyDetails.phone}</Text>}
            {companyDetails.email && <Text>{companyDetails.email}</Text>}
            {companyDetails.website && <Text>{companyDetails.website}</Text>}
          </View>
        </View>
        <View style={styles.split}>
          <View style={styles.addressBox}>
            <Text>Billing Address</Text>
            <Text>
              {proposal.firstName} {proposal.lastName}
            </Text>
            <Text>{proposal.addressLine1}</Text>
            <Text>{proposal.addressLine2}</Text>
            <Text>
              {proposal.city}, {proposal.state} {proposal.zip}
            </Text>
          </View>
          <View style={styles.addressBox}>
            <Text>Shipping Address</Text>
            <Text>
              {proposal.firstName} {proposal.lastName}
            </Text>
            <Text>{proposal.serviceAddressLine1}</Text>
            <Text>{proposal.serviceAddressLine2}</Text>
            <Text>
              {proposal.serviceCity}, {proposal.serviceState}{" "}
              {proposal.serviceZip}
            </Text>
          </View>
        </View>
        <View style={styles.section}>
          <View style={styles.proposalLine}>
            <Text style={styles.proposalLineInfo}>Item</Text>
            <Text style={styles.proposalLineQty}>Qty.</Text>
            <Text style={styles.proposalLinePrice}>Unit Price</Text>
            <Text style={styles.proposalLineTotal}>Amount</Text>
          </View>
          {proposal.lines.map((l) => {
            return (
              <View style={styles.proposalLine}>
                <View style={styles.proposalLineInfo}>
                  <Text style={styles.proposalLineProduct}>
                    {l.productName}
                  </Text>
                  <Text style={styles.proposalLineNotes}>{l.productNotes}</Text>
                </View>
                <Text style={styles.proposalLineQty}>{l.quantity}</Text>
                <Text style={styles.proposalLinePrice}>
                  {formatMoney(l.unitPrice)}
                </Text>
                <Text style={styles.proposalLineTotal}>
                  {formatMoney(l.quantity * l.unitPrice)}
                </Text>
              </View>
            );
          })}
          <View style={styles.proposalSummary}>
            <View style={styles.proposalSummaryLine}>
              <Text style={styles.proposalSummaryDesc}>Subtotal:</Text>
              <Text style={styles.proposalSummaryAmount}>
                {formatMoney(proposal.subtotalAmount)}
              </Text>
            </View>
            <View style={styles.proposalSummaryLine}>
              <Text style={styles.proposalSummaryDesc}>Tax:</Text>
              <Text style={styles.proposalSummaryAmount}>
                {formatMoney(proposal.taxAmount)}
              </Text>
            </View>
            <View style={styles.proposalSummaryLine}>
              <Text style={styles.proposalSummaryDesc}>Total:</Text>
              <Text style={styles.proposalSummaryAmount}>
                {formatMoney(proposal.totalAmount)}
              </Text>
            </View>
          </View>
        </View>
        <View style={styles.terms}>
          <Text>{proposal.terms}</Text>
        </View>
        <View style={styles.section}>
          {proposal.signature ? (
            <Image style={styles.signatureImg} src={proposal.signature}></Image>
          ) : (
            <Text style={styles.signatureLine}>X</Text>
          )}
        </View>
      </Page>
    </Document>
  );
}
